import { Box } from '../Box';
import { useDirection } from '@front10/utils';
import { getDotsToShow } from './dots';
import * as React from 'react';
import { KeyboardEvent, MouseEvent } from 'react';
import Slider, { Settings as SlickSettings } from 'react-slick';
import { BasicNavigationLayout } from './atoms/BasicNavigationLayout';
import { Dot } from './atoms/Dot';
import { NavigationButton } from './atoms/NavigationButton';

const getNewIndex = ({
  index,
  slidesToShow,
  itemsSize,
  direction
}: {
  index: number;
  slidesToShow: number;
  itemsSize: number;
  direction: 'ltr' | 'rtl';
}) => {
  let newIndex = Math.trunc(index);
  if (direction === 'ltr' && newIndex + Math.trunc(slidesToShow) > itemsSize - 1) {
    newIndex = itemsSize - Math.trunc(slidesToShow);
  }
  if (direction === 'rtl') {
    newIndex = itemsSize - index - slidesToShow;
    newIndex = newIndex < 0 ? 0 : newIndex;
  }

  return newIndex;
};

export type CarouselProps = React.PropsWithChildren<
  Props &
    Pick<
      SlickProps,
      | 'autoplay'
      | 'autoplaySpeed'
      | 'infinite'
      | 'draggable'
      | 'initialSlide'
      | 'pauseOnDotsHover'
      | 'pauseOnFocus'
      | 'pauseOnHover'
      | 'speed'
      | 'lazyLoad'
    >
>;

function Carousel({
  autoplay,
  autoplaySpeed = 3000,
  infinite,
  draggable = true,
  pauseOnDotsHover = true,
  pauseOnFocus = true,
  pauseOnHover = true,
  speed = 500,
  initialSlide = 0,
  elementsToShow: slidesToShow_ = 4,
  scrolling = 'frame',
  controlsOffset = 0,
  gap = '6px',
  maxDotsToShow = 10,
  showDots = true,
  showNavigation = true,
  swipe = true,
  ...props
}: CarouselProps) {
  const direction = useDirection();
  const [currentSlide, setCurrentSlide] = React.useState(initialSlide);

  const containerRef = React.useRef<HTMLDivElement>(null);
  const carouselRef = React.useRef<Slider>(null!);
  const isKeyboardNavigator = React.useRef(false);

  const slidesToShow =
    slidesToShow_ && slidesToShow_ >= 2 && slidesToShow_ <= 4
      ? slidesToShow_
      : slidesToShow_ - Math.floor(slidesToShow_) !== 0
      ? slidesToShow_
      : 4;

  const frameScrollSize = Math.trunc(slidesToShow);
  const slidesToScroll = scrolling === 'frame' ? frameScrollSize : 1;
  const items = React.Children.toArray(props.children) as React.ReactElement[];
  const itemsLength = items.length;

  const columnCount: number = Math.floor(itemsLength - 1) + 1;
  const firstSlide = direction === 'ltr' ? 0 : columnCount - 1;
  const lastSlide = direction === 'ltr' ? columnCount - 1 - (slidesToShow - 1) : 0;
  const lazyLoad = props.lazyLoad ?? (itemsLength > 20 ? 'progressive' : undefined);

  // Update carousel position when data changed
  React.useEffect(() => {
    function refresh() {
      setCurrentSlide(initialSlide);
      carouselRef.current?.slickGoTo(
        direction === 'rtl' ? columnCount - initialSlide - 1 : initialSlide,
        true
      );
    }

    refresh();
    // TODO: Refresh once more after a time, as slick doesn't
    // seem to update immediately in some circumstances,
    // not even using useLayoutEffect.
    setTimeout(() => refresh(), 500);
  }, [direction, columnCount, initialSlide]);

  const handleAfterChange = (index: number) => {
    const newIndex = getNewIndex({
      direction,
      index,
      slidesToShow,
      itemsSize: columnCount
    });
    if (currentSlide !== newIndex) {
      setCurrentSlide(newIndex);
    }
  };

  // Define if user is navigate by keyboard
  const activeKeyboard = () => {
    isKeyboardNavigator.current = true;
  };

  const handleNextPress = (_event: MouseEvent<HTMLButtonElement>) => {
    carouselRef.current.slickNext();
  };
  const handleNextKeyPress = (_event: KeyboardEvent<HTMLButtonElement>) => {
    carouselRef.current.slickNext();
    activeKeyboard();
  };

  const handlePreviousPress = (_event: MouseEvent<HTMLButtonElement>) => {
    carouselRef.current.slickPrev();
  };
  const handlePreviousKeyPress = (_event: KeyboardEvent<HTMLButtonElement>) => {
    carouselRef.current.slickPrev();
    activeKeyboard();
  };

  let prevButton = (
    <NavigationButton
      icon="left-chevron"
      disabled={currentSlide === firstSlide}
      onPressAction={handlePreviousPress}
      onKeyboardAction={handlePreviousKeyPress}
      label="Previous slide"
    />
  );

  let nextButton = (
    <NavigationButton
      icon="right-chevron"
      disabled={currentSlide >= lastSlide}
      onPressAction={handleNextPress}
      onKeyboardAction={handleNextKeyPress}
      label="Next slide"
    />
  );

  if (direction === 'rtl') {
    const tempButton = prevButton;
    prevButton = nextButton;
    nextButton = tempButton;
  }

  const controlsPosition =
    typeof controlsOffset === 'number'
      ? `${12 + controlsOffset}px`
      : controlsOffset.map((value) => `${12 + value}px`);

  const framesToShow =
    columnCount <= slidesToShow ? 1 : Math.ceil((columnCount - slidesToShow) / slidesToScroll) + 1;
  const currentFrame =
    currentSlide < slidesToShow ? 0 : 1 + Math.ceil((currentSlide - slidesToShow) / slidesToScroll);
  const [firstDot, lastDot] = getDotsToShow({
    index: currentFrame,
    maxDotsToShow,
    numberOfDots: framesToShow
  });

  // Define slick settings
  const settings: React.ComponentPropsWithoutRef<typeof Slider> = {
    dots: showDots,
    arrows: showNavigation,
    infinite: infinite ?? autoplay,
    slidesToShow,
    slidesToScroll,
    autoplay,
    autoplaySpeed,
    draggable,
    pauseOnDotsHover,
    pauseOnFocus,
    pauseOnHover,
    speed,
    initialSlide,
    swipe,
    beforeChange: (_current, next) => {
      // calling it here instead of inside afterChange because
      // afterChange is not called when carousel dimensions change
      // which happens to us when lazy loading is active.
      // https://github.com/akiran/react-slick/issues/1262
      handleAfterChange(next);
    },
    lazyLoad,
    prevArrow: <BasicNavigationLayout left={controlsPosition} children={prevButton} />,
    nextArrow: <BasicNavigationLayout right={controlsPosition} children={nextButton} />,
    appendDots: (dots) => {
      const dotsToShow =
        framesToShow > maxDotsToShow
          ? React.Children.toArray(dots).filter((_elem, i) => {
              return i >= firstDot && i <= lastDot;
            })
          : dots;
      return (
        <Box data-fr-reel sx={{ textAlign: 'center', marginTop: '0px' }}>
          <ul>{dotsToShow}</ul>
        </Box>
      );
    },
    customPaging: function PagingDot(i) {
      return <Dot pageNumber={i} />;
    }
  };

  const cardGap = gap;
  const cardPadding = `calc(${cardGap} / 2)`;

  return (
    <Box
      dir={direction}
      data-fr-carousel
      className="Carousel"
      role="application"
      sx={{
        height: 'fit-content !important',
        '--shadow-space': props.unstable_noPaddingBottom && !showDots ? '0px' : '10px',
        '--carousel-dots-height': showDots ? 'calc(25px - var(--shadow-space))' : undefined,
        '.slick-list': {
          marginLeft: '0px',
          marginRight: '0px'
        },
        '.slick-arrow': {
          pointerEvents: 'none',
          '& button': {
            pointerEvents: 'auto'
          }
        },
        '.slick-track': {
          display: 'flex',
          paddingBottom: 'var(--shadow-space)',
          mx: 0
        },
        '.slick-slide': {
          height: 'auto'
        },
        '.slick-slider .slick-list .slick-track .slick-slide > div': {
          px: cardPadding
        }
      }}
      ref={containerRef}
    >
      <Slider {...settings} ref={carouselRef}>
        {items.map((item, index) => (
          <React.Fragment key={index}>{item}</React.Fragment>
        ))}
      </Slider>
    </Box>
  );
}

type SlickProps = Pick<
  SlickSettings,
  | 'responsive'
  | 'autoplay'
  | 'autoplaySpeed'
  | 'draggable'
  | 'infinite'
  | 'initialSlide'
  | 'pauseOnDotsHover'
  | 'pauseOnFocus'
  | 'pauseOnHover'
  | 'speed'
  | 'lazyLoad'
>;

interface Props {
  /**
   * Show dots in the component
   */
  showDots?: boolean;
  swipe?: boolean;
  /**
   * show Navigation Buttons
   */
  showNavigation?: boolean;
  /**
   * How many card to show in one frame
   */
  elementsToShow?: number;
  /**
   * Scrolling behavior
   * - frame: scroll all visible cards (default)
   * - slide: scroll one card at a time
   */
  scrolling?: 'frame' | 'card';
  controlsOffset?: number | number[];
  gap?: string;
  customCss?: string;
  maxDotsToShow?: number;
  unstable_noPaddingBottom?: boolean;
}

export { Carousel };
